"use client";
import { useEffect, useState } from "react";
import { Button } from "@/components/atoms/button";
import { ArrowPathIcon, SignalSlashIcon } from "@heroicons/react/20/solid";

export default function ErrorPage({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    console.error(error);
  }, [error]);

  const useInternetStatus = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [serverReachable, setServerReachable] = useState(false);
    const checkServer = async () => {
      try {
        const response = await fetch("/api/health", { method: "HEAD" });
        setServerReachable(response.ok);
      } catch {
        setServerReachable(false);
      }
    };

    useEffect(() => {
      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
      checkServer();
      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      };
    }, []);
    return { isOnline, serverReachable };
  };

  const { isOnline, serverReachable } = useInternetStatus();

  if (!isOnline) {
    return (
      <div className="flex flex-col space-y-4 items-center justify-center h-[70vh] text-center">
        <SignalSlashIcon className="h-24 w-24 text-primary" />
        <h2 className="text-3xl font-bold text-black">
          It seems you're offline
        </h2>
        <p className="text-lg text-gray-600">
          Please check your internet connection and try again
        </p>
        <Button
          onClick={() => window.location.reload()}
          outline
          className="w-auto text-lg">
          Try again
          <ArrowPathIcon />
        </Button>
      </div>
    );
  }

  if (!serverReachable) {
    return (
      <div className="flex flex-col space-y-4 items-center justify-center h-[70vh] text-center">
        <SignalSlashIcon className="h-24 w-24 text-primary" />
        <h2 className="text-3xl font-bold text-black">Server Unreachable</h2>
        <p className="text-lg text-gray-600">
          The server is currently unavailable, please try again later
        </p>
        <Button
          onClick={() => window.location.reload()}
          outline
          className="w-auto text-lg">
          Try again
          <ArrowPathIcon />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-4 items-center justify-center h-[70vh] text-center">
      <h2 className="text-3xl font-bold text-black">An error occurred</h2>
      <p className="text-lg text-gray-600 italics py-3">{error.message}</p>
      <Button
        onClick={() => window.location.reload()}
        outline
        className="w-auto text-lg">
        Try again
        <ArrowPathIcon />
      </Button>
    </div>
  );
}
